import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import sanityClient from "../../client";
import Masonry from "react-masonry-css";
import "./AviationPage.css";
import Mixpanel from "../../lib/mixpanel";
import Seo from "../../components/Seo";

export default function AviationPage() {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Track page view
    Mixpanel.trackAviation();
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const data = await sanityClient.fetch(
          `*[_type == "aviation-images"] {
            Title,
            image {
              asset-> {
                url,
                metadata {
                  dimensions
                }
              }
            }
          }`
        );
        setImages(data);
        if (data.length === 0) {
          setError(true);
        }
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Build an optimized Sanity CDN URL (resized + WebP + compressed) from the
  // raw asset URL. The originals are multi-MB; this serves web-sized files.
  const optimizedUrl = (url, width) =>
    `${url}?w=${width}&fit=max&auto=format&q=75`;

  // Configure responsive breakpoints for masonry - consistent columns for VSCO look
  const breakpointColumnsObj = {
    default: 3,
    1100: 3,
    700: 2,
    500: 2, // Always 2 columns on mobile for authentic VSCO grid
    320: 2, // Maintain 2 columns even on small devices
  };

  return (
    <div className="min-h-screen bg-background text-darkgray">
      <Seo
        title="Aviation"
        path="/aviation"
        description="Aviation photography by Krushil Amrutiya — a gallery capturing his passion for flight and planes."
      />
      <div>
        <div className="sticky top-0 z-50 bg-background/85 backdrop-blur-md border-b border-border">
          <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
            <h1 className="text-[15px] font-medium tracking-tight text-darkgray">
              Aviation
            </h1>
            <Link
              to="/"
              className="text-[13px] text-lightgray hover:text-darkgray transition-colors focus-ring rounded-sm"
            >
              Home
            </Link>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-3 py-4">
          {isLoading ? (
            <div className="loading-container">
              <div className="text-gray flex flex-col items-center">
                <i className="bx bx-loader-alt text-3xl animate-spin text-orange mb-2"></i>
                <p>Loading images...</p>
              </div>
            </div>
          ) : error ? (
            <div className="loading-container">
              <p className="text-[15px] text-gray text-center px-6">
                Aviation images are currently unavailable. I'm working on
                restoring them soon :)
              </p>
            </div>
          ) : (
            <div className="masonry-container">
              <Masonry
                breakpointCols={breakpointColumnsObj}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
              >
                {images.map((imageItem, index) => {
                  const rawUrl = imageItem.image.asset.url;
                  const dims = imageItem.image.asset.metadata?.dimensions;
                  return (
                    <div key={index} className="masonry-item">
                      <div className="image-wrapper">
                        <img
                          src={optimizedUrl(rawUrl, 800)}
                          srcSet={`${optimizedUrl(rawUrl, 400)} 400w, ${optimizedUrl(
                            rawUrl,
                            800
                          )} 800w, ${optimizedUrl(rawUrl, 1200)} 1200w`}
                          sizes="(max-width: 700px) 50vw, 33vw"
                          width={dims?.width}
                          height={dims?.height}
                          alt={imageItem.Title || "Aviation image"}
                          className="image-hover-effect"
                          loading={index < 6 ? "eager" : "lazy"}
                          decoding="async"
                          fetchPriority={index < 6 ? "high" : "auto"}
                        />
                        <div className="image-overlay">
                          <h3 className="text-white text-sm md:text-base">
                            {imageItem.Title}
                          </h3>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Masonry>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
